import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { primeQuack, playQuack } from "../lib/quack";

const form = (fields) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => v !== undefined && v !== null && v !== "" && fd.append(k, v));
  return fd;
};

// A spend-gate refusal (HTTP 202 {queued:true}) arrives as a QueuedByGate rejection, never as a duck: show its
// queue wording rather than the call's generic failure text.
const detailOf = (e, fallback) => {
  if (e?.queued) return e.message;
  const d = e?.response?.data?.detail;
  return (Array.isArray(d) ? d[0]?.msg : d) || fallback;
};

// Every mutation of the current creation: generate, regenerate, variations, select, approve, cancel, extend, notify, gallery ops.
export const useCreationActions = ({ current, setCurrent, setStep, loadGallery }) => {
  const [busy, setBusy] = useState(null); // 'regenerate' | 'variations' | 'approve'
  const [uploading, setUploading] = useState(false);
  const keepPhoto = (data) => setCurrent((c) => ({ ...data, photo: data.photo || c?.photo }));
  const reset = () => {
    setCurrent(null);
    setStep(1);
  };

  const mutate = async (key, request, { ok, err } = {}) => {
    if (!current) return;
    if (key) setBusy(key);
    try {
      const { data } = await request(current.id);
      keepPhoto(data);
      ok?.(data);
    } catch (e) {
      if (e?.response?.status === 409) {
        try { const { data } = await api.get(`/creations/${current.id}`); keepPhoto(data); } catch { /* retain current preview */ }
      }
      toast.error(detailOf(e, err));
    } finally {
      if (key) setBusy(null);
    }
  };

  const generate = async ({ file, style, figure, name, logo, logoPlacement, asRender, useLibrary = true, styleRefCount, faithful }) => {
    setUploading(true);
    try {
      const fields = asRender
        ? { render: file, figure, name, faithful: faithful === false ? false : true }
        : { photo: file, style, figure, name, logo, logo_placement: logo ? logoPlacement : null, use_library: useLibrary, style_ref_count: styleRefCount };
      const { data } = await api.post("/creations", form(fields));
      if (asRender) {
        // The maker already has a finished render — skip the front-render approval and go straight to drawing the back and sides.
        primeQuack();
        let approved;
        try {
          ({ data: approved } = await api.post(`/creations/${data.id}/approve`, { public_base: window.location.origin }));
        } catch (e) {
          if (e?.response?.data?.code !== "broken_views") throw e;
          // The view check holds this upload: show why and the ways forward (step 2), nothing was charged.
          const { data: held } = await api.get(`/creations/${data.id}`);
          setCurrent(held);
          setStep(2);
          loadGallery();
          return;
        }
        setCurrent(approved);
        setStep(3);
        loadGallery();
        playQuack("upload", { once: `${data.id}:upload` }); // Step 01 — uploaded render accepted, flow moves on
        toast.success("Render uploaded — drawing the back and sides…");
      } else {
        // 202: the duck exists, the render runs in the background. Remember it so a refresh resumes here.
        try { localStorage.setItem("duckify_current_duck", data.id); } catch { /* private mode */ }
        setCurrent(data);
        setStep(2);
        loadGallery();
        playQuack("upload", { once: `${data.id}:upload` }); // Step 01 — photo accepted, flow moves on to the render
        toast.success(data.render ? "Duck render generated! Approve it or regenerate." : "Making your duck — this takes about a minute.");
      }
    } catch (e) {
      if (e?.queued) {
        toast.info(e.message);                     // parked by the spend gate: no duck was created, nothing to show
      } else {
        toast.error(detailOf(e, asRender ? "Could not use that image. Try a PNG or JPG." : "Could not generate the render. Try another photo."));
      }
    } finally {
      setUploading(false);
    }
  };

  const branch = async () => {
    if (!current) return;
    setBusy("branch");
    try {
      const { data } = await api.post(`/creations/${current.id}/branch`);
      setCurrent(data);
      setStep(2);
      loadGallery();
      toast.success(`Branched — version ${data.version} rendered. Approve it or regenerate.`);
    } catch (e) {
      toast.error(detailOf(e, "Could not branch this duck. Try again."));
    } finally {
      setBusy(null);
    }
  };

  const regenerate = (style, figure, note) =>
    mutate("regenerate", (id) => api.post(`/creations/${id}/regenerate`, form({ style, figure, note })), {
      ok: () => {
        loadGallery();
        toast.success(note ? "Regenerated with your notes!" : "Fresh take on your duck!");
      },
      err: "Regeneration failed. Try again.",
    });

  const variations = () =>
    mutate("variations", (id) => api.post(`/creations/${id}/variations`), {
      ok: () => toast.success("More options ready — pick your favorite!"),
      err: "Could not generate more options. Try again.",
    });

  const select = (index) =>
    mutate(null, (id) => api.post(`/creations/${id}/select`, form({ index: String(index) })), { ok: loadGallery, err: "Could not select that option." });

  // Step-2 style picker: generate the 3 face styles on demand (returns the images), then lock one in as the render to sculpt.
  const generateStyles = async (note) => {
    if (!current) return null;
    try {
      const { data } = await api.post(`/creations/${current.id}/styles`, form({ note }));
      return data.styles;
    } catch (e) {
      toast.error(detailOf(e, "Could not render the styles. Try again."));
      return null;
    }
  };

  const selectStyle = (styleId) =>
    mutate(null, (id) => api.post(`/creations/${id}/styles/select`, form({ style: styleId })), {
      ok: () => {
        loadGallery();
        toast.success(`Locked in the ${styleId} style.`);
      },
      err: "Could not select that style.",
    });

  const approve = () => {
    primeQuack();
    return mutate("approve", (id) => api.post(`/creations/${id}/approve`, { public_base: window.location.origin }), {
      ok: () => {
        setStep(3);
        loadGallery();
        toast.success("Render approved — sculpting your 3D figurine…");
      },
      err: "Could not start the 3D build. Try again.",
    });
  };

  // The explicit "Retry with a stronger sculptor": the server drops the banked sculpt so a fresh one is made.
  const retryStronger = () => {
    primeQuack();
    return mutate("approve", (id) => api.post(`/creations/${id}/approve`, { public_base: window.location.origin, stronger: true }), {
      ok: () => {
        setStep(3);
        loadGallery();
        toast.success("Starting a fresh sculpt with the stronger sculptor…");
      },
      err: "Could not start the 3D build. Try again.",
    });
  };

  const cancel = () =>
    mutate(null, (id) => api.post(`/creations/${id}/cancel`), {
      ok: () => {
        loadGallery();
        toast("3D build cancelled.");
      },
      err: "Could not cancel the build.",
    });

  const extend = () => mutate(null, (id) => api.post(`/creations/${id}/extend`), { ok: () => toast("Added 10 more minutes to this build."), err: "Could not extend the build." });

  const notify = (email) =>
    mutate(null, (id) => api.post(`/creations/${id}/notify`, { email, public_base: window.location.origin }), {
      ok: () => toast.success(`We'll email ${email} when your duck is ready.`),
      err: "Please enter a valid email address.",
    });

  const openItem = async (c) => {
    try {
      const { data } = await api.get(`/creations/${c.id}`);
      setCurrent(data);
      setStep(data.status === "approved" ? 3 : 2);
    } catch (e) {
      toast.error("Could not open this duck.");
    }
  };

  const deleteItem = async (id) => {
    await api.delete(`/creations/${id}`);
    if (current?.id === id) reset();
    loadGallery();
    toast.success("Duck removed.");
  };

  const clearGallery = async () => {
    await api.delete("/creations");
    reset();
    loadGallery();
    toast.success("Gallery cleared.");
  };

  return { busy, uploading, keepPhoto, reset, generate, branch, regenerate, variations, select, generateStyles, selectStyle, approve, retryStronger, cancel, extend, notify, openItem, deleteItem, clearGallery };
};
